import { useRef, useState } from "react";
import { FileTextIcon, SparklesIcon, Trash2Icon, UploadIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { CODIGO_TASACION_CLIENTE, CODIGO_TASACION_CONSULTEK } from "@/lib/informes";
import { cn } from "@/lib/utils";

import { formatearPeso } from "./ZonaDeImagen";

/** Espejo de `ImportarTasacionRequest::MAXIMO_KB`. */
export const MAXIMO_BYTES_DE_LA_TASACION = 12 * 1024 * 1024;

/** De quién es la tasación que se está importando. */
export type OrigenDeLaTasacion =
  | typeof CODIGO_TASACION_CONSULTEK
  | typeof CODIGO_TASACION_CLIENTE;

/**
 * Las dos únicas opciones, y son DOS y no tres.
 *
 * "Otros documentos descriptivos de la propiedad" es la tercera categoría de
 * antecedentes históricos, pero no entra acá: lo que se importa es una TASACIÓN,
 * y de eso solo hay dos procedencias posibles. Ofrecer la tercera invitaría a
 * importar un plano o una escritura, de los que no se pueden sacar estos datos.
 */
const ORIGENES: { codigo: OrigenDeLaTasacion; etiqueta: string }[] = [
  { codigo: CODIGO_TASACION_CONSULTEK, etiqueta: "Realizada por Consultek" },
  { codigo: CODIGO_TASACION_CLIENTE, etiqueta: "Entregada por el cliente" },
];

/**
 * Lo que el navegador puede rechazar sin gastar la subida, o `null`.
 *
 * No es la autoridad —la validación real mira los bytes con finfo— pero evita
 * mandar 12 MB por la red para enterarse después.
 */
export function motivoDeRechazoDeLaTasacion(archivo: File): string | null {
  if (archivo.type !== "application/pdf") {
    return "La tasación anterior debe ser un PDF.";
  }
  if (archivo.size > MAXIMO_BYTES_DE_LA_TASACION) {
    return "No podemos leer un PDF de más de 12 MB. Crea el informe y adjúntalo desde Antecedentes históricos.";
  }
  return null;
}

/**
 * Importar una tasación anterior para precargar el formulario.
 *
 * SOLO EXISTE EN LA CREACIÓN DEL INFORME, y esa es la regla que ordena todo lo
 * demás. Es una AYUDA DE CARGA: sirve mientras el formulario está vacío. Una vez
 * creado el informe los datos ya están cargados y revisados por una persona, así
 * que volver a ofrecerla sería ofrecer pisarlos. Lo que queda después es
 * adjuntar desde la pestaña de antecedentes históricos, que es registro y no
 * ayuda — otra cosa, en otra pantalla, con otro botón.
 *
 * ES UN SOLO DOCUMENTO Y HAY QUE DECLARAR CUÁL ES. La tasación que se importa
 * es de Consultek o de un tercero, nunca de las dos, y esa elección no se puede
 * deducir del archivo: son PDFs de empresas distintas sin nada que los marque.
 * Sin declararla, el documento quedaría adjunto en la categoría equivocada —o en
 * ninguna— y el antecedente histórico diría algo falso sobre su procedencia.
 *
 * Presentacional, como {@see ZonaDeImagen}: no sabe de la API. Quien llama es la
 * pantalla de creación, que es la que puede precargar el formulario con la
 * respuesta y adjuntar el archivo después del alta.
 */
export function ImportadorDeTasacion({
  origen,
  archivo,
  leyendo = false,
  resumen,
  error,
  onOrigen,
  onArchivo,
  onQuitar,
}: {
  origen: OrigenDeLaTasacion;
  /** El PDF elegido, retenido hasta que el informe exista. */
  archivo: File | null;
  /** Hay una lectura en curso. */
  leyendo?: boolean;
  /** Qué se precargó, en una línea. `null` mientras no se haya leído nada. */
  resumen?: string | null;
  error?: string | null;
  onOrigen: (origen: OrigenDeLaTasacion) => void;
  onArchivo: (archivo: File) => void;
  onQuitar: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [arrastrando, setArrastrando] = useState(false);

  return (
    <section className="bg-card space-y-4 rounded-xl border p-5">
      <div className="flex items-start gap-3">
        <SparklesIcon className="text-primary mt-0.5 size-5 shrink-0" aria-hidden="true" />
        <div>
          <h2 className="text-h3 text-ink">Importar una tasación anterior</h2>
          <p className="text-ink-muted text-caption">
            Sube el PDF de una tasación previa de esta propiedad y completamos con ella los
            datos que encontremos. Revísalos antes de crear el informe: el documento queda
            adjunto como antecedente histórico.
          </p>
        </div>
      </div>

      {/* La procedencia va ANTES del archivo y no después, porque es lo que
          decide dónde queda adjunto: preguntarla al final dejaría a alguien
          subiendo el documento sin saber que tenía que declararla. */}
      <fieldset className="space-y-2">
        <legend className="text-ink text-xs font-bold">¿De quién es esta tasación?</legend>
        <div className="bg-muted/60 inline-flex rounded-md p-0.5" role="group">
          {ORIGENES.map((opcion) => (
            <button
              key={opcion.codigo}
              type="button"
              aria-pressed={origen === opcion.codigo}
              disabled={leyendo || archivo !== null}
              onClick={() => onOrigen(opcion.codigo)}
              className={cn(
                "rounded-[0.625rem] px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-50",
                origen === opcion.codigo
                  ? "bg-card text-ink shadow-sm"
                  : "text-ink-muted hover:text-ink",
              )}
            >
              {opcion.etiqueta}
            </button>
          ))}
        </div>
        {archivo !== null ? (
          // Cambiar la procedencia con el archivo ya leído dejaría el documento
          // adjunto en una categoría distinta de la que se declaró al leerlo.
          // Se quita el archivo y se vuelve a empezar, que son dos clics.
          <p className="text-ink-hint text-xs">
            Para cambiarla, quita el documento y vuelve a subirlo.
          </p>
        ) : null}
      </fieldset>

      {archivo === null ? (
        <div
          role="button"
          tabIndex={0}
          aria-label="Subir el PDF de la tasación anterior"
          onClick={() => input.current?.click()}
          onKeyDown={(evento) => {
            if (evento.key === "Enter" || evento.key === " ") {
              evento.preventDefault();
              input.current?.click();
            }
          }}
          onDragOver={(evento) => {
            evento.preventDefault();
            setArrastrando(true);
          }}
          onDragLeave={() => setArrastrando(false)}
          onDrop={(evento) => {
            evento.preventDefault();
            setArrastrando(false);
            const soltado = evento.dataTransfer.files?.[0];
            if (soltado) onArchivo(soltado);
          }}
          className={cn(
            "flex cursor-pointer flex-col items-center gap-2 rounded-md border-[1.5px] border-dashed px-5 py-8 text-center transition-colors",
            "focus-visible:border-ring focus-visible:ring-ring/50 outline-none focus-visible:ring-[3px]",
            arrastrando ? "border-primary bg-primary/5" : "border-input hover:bg-muted/40",
          )}
        >
          {leyendo ? <Spinner /> : <UploadIcon className="text-ink-hint size-6" aria-hidden="true" />}
          <span className="text-ink text-sm font-semibold">
            {leyendo ? "Leyendo el documento…" : "Arrastra el PDF o haz clic para elegirlo"}
          </span>
          <span className="text-ink-hint text-xs">PDF de hasta 12 MB</span>
        </div>
      ) : leyendo ? (
        /* EL ESTADO DE ESPERA TIENE QUE VIVIR ACÁ, en la rama del archivo ya
           elegido, y no en la del recuadro de arrastre: el archivo se retiene
           ANTES de empezar a leerlo, así que durante toda la lectura `archivo`
           ya no es null y aquella rama no se dibuja nunca. Con el indicador del
           otro lado, la persona pasaba veinte segundos mirando el nombre de su
           archivo sin una sola señal de que algo estuviera pasando.

           `role="status"` con `aria-live="polite"`: una espera larga y muda es
           peor sin vista que con ella, y sin esto un lector de pantalla no
           anuncia nada entre que se suelta el archivo y que aparecen los datos. */
        <div
          role="status"
          aria-live="polite"
          className="border-primary/30 bg-primary/5 flex items-center gap-3 rounded-md border-[1.5px] px-3 py-3"
        >
          {/* El spinner del DS trae su propio `role="status"` y su etiqueta
              "Loading". Acá es DECORATIVO —el texto de al lado es el que dice
              qué está pasando, y en español— así que se lo esconde de la
              tecnología asistiva: si no, hay dos regiones de estado anidadas y
              el lector de pantalla anuncia "Loading" antes que la frase útil. */}
          <Spinner role={undefined} aria-label={undefined} aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-ink truncate text-sm font-semibold">
              Leyendo {archivo.name}…
            </p>
            {/* Decir CUÁNTO puede tardar es la mitad del indicador. Un informe
                de tasación de 36 páginas se lee en unos veinte segundos, y sin
                el aviso ese tiempo se siente como que la pantalla se colgó. */}
            <p className="text-ink-hint text-xs">
              Estamos extrayendo los datos de la propiedad. En un informe largo puede tardar
              cerca de un minuto.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-muted/40 flex items-center gap-2 rounded-md px-3 py-2">
          <FileTextIcon className="text-ink-hint size-4 shrink-0" aria-hidden="true" />
          <span className="text-ink truncate text-sm font-semibold">{archivo.name}</span>
          <span className="text-ink-hint ml-auto shrink-0 font-mono text-xs">
            {formatearPeso(archivo.size)}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onQuitar}
            aria-label={`Quitar ${archivo.name}`}
          >
            <Trash2Icon aria-hidden="true" />
          </Button>
        </div>
      )}

      {/* El input va oculto y lo dispara el recuadro: el control nativo no se
          puede estilar. `sr-only` y no `display:none` para que siga existiendo
          para la tecnología asistiva. */}
      <input
        ref={input}
        type="file"
        accept=".pdf"
        className="sr-only"
        aria-label="PDF de la tasación anterior"
        onChange={(evento) => {
          const elegido = evento.target.files?.[0];
          // Se limpia SIEMPRE, incluso cuando se acepta: sin esto, volver a
          // elegir el mismo archivo no dispara `change` —el valor no cambió— y
          // reintentar tras un error del servidor sería imposible.
          evento.target.value = "";
          if (elegido) onArchivo(elegido);
        }}
      />

      {resumen ? <p className="text-success-soft-foreground text-sm">{resumen}</p> : null}
      {error ? <p className="text-destructive text-sm">{error}</p> : null}
    </section>
  );
}
